<template>
  <teleport to="body">
    <transition name="modal-fade" @after-leave="restoreReleasedFocus">
      <div
        v-if="visible"
        ref="backdrop"
        class="modal-backdrop"
        :class="`modal-backdrop--${placement}`"
        @click.self="handleBackdrop"
        @keydown="handleKeydown"
      >
        <div
          ref="dialog"
          class="modal-container"
          :class="`modal-container--${placement}`"
          :style="computedWidth"
          role="dialog"
          :aria-label="resolvedAriaLabel"
          :aria-labelledby="resolvedLabelledby"
          :aria-describedby="resolvedDescribedby"
          aria-modal="true"
          tabindex="-1"
          @click.stop
        >
          <header class="modal-header">
            <slot name="header">
              <h3 v-if="title" :id="titleId" class="modal-title">
                {{ title }}
              </h3>
            </slot>
            <button
              v-if="showCloseButton"
              class="modal-close"
              type="button"
              aria-label="Close"
              @click="requestClose"
            >
              <svg
                class="modal-close__icon"
                aria-hidden="true"
                focusable="false"
              >
                <use href="@/assets/icons/iconset.svg#cross"></use>
              </svg>
            </button>
          </header>
          <div class="modal-body">
            <p v-if="description" :id="descriptionId" class="modal-description">
              {{ description }}
            </p>
            <slot></slot>
          </div>
          <footer v-if="$slots.footer" class="modal-footer">
            <slot name="footer"></slot>
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
let bodyScrollLockCount = 0;
let bodyOverflowBeforeFirstLock = "";
let modalInstanceCount = 0;
let focusBeforeDialogStack = null;
const openDialogStack = [];
let interactionTrackerCount = 0;
let lastActivatedElement = null;
let interactionClearTimer = null;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const trackActivationTarget = (event) => {
  const eventTarget = event.composedPath?.()[0] || event.target;
  const candidate = eventTarget?.closest?.(FOCUSABLE_SELECTOR);
  lastActivatedElement = candidate?.focus ? candidate : null;

  if (interactionClearTimer) clearTimeout(interactionClearTimer);
  interactionClearTimer = setTimeout(() => {
    lastActivatedElement = null;
    interactionClearTimer = null;
  }, 0);
};

const startInteractionTracking = () => {
  if (typeof document === "undefined") return;
  if (interactionTrackerCount === 0) {
    document.addEventListener("click", trackActivationTarget, true);
  }
  interactionTrackerCount += 1;
};

const stopInteractionTracking = () => {
  if (typeof document === "undefined" || interactionTrackerCount === 0) return;
  interactionTrackerCount -= 1;
  if (interactionTrackerCount !== 0) return;

  document.removeEventListener("click", trackActivationTarget, true);
  if (interactionClearTimer) clearTimeout(interactionClearTimer);
  interactionClearTimer = null;
  lastActivatedElement = null;
};

const lockBodyScroll = () => {
  if (typeof document === "undefined") return;
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeFirstLock = document.body.style.overflow;
  }
  bodyScrollLockCount += 1;
  document.body.style.overflow = "hidden";
};

const unlockBodyScroll = () => {
  if (typeof document === "undefined" || bodyScrollLockCount === 0) return;
  bodyScrollLockCount -= 1;
  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = bodyOverflowBeforeFirstLock;
    bodyOverflowBeforeFirstLock = "";
  }
};

const isConnectedFocusable = (element) =>
  Boolean(element?.focus && element.isConnected !== false);

export default {
  name: "Modal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    ariaLabelledby: {
      type: String,
      default: "",
    },
    ariaDescribedby: {
      type: String,
      default: "",
    },
    placement: {
      type: String,
      default: "center",
      validator: (value) => ["center", "sheet"].includes(value),
    },
    width: {
      type: String,
      default: "640px",
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    initialFocusSelector: {
      type: String,
      default: "",
    },
  },
  emits: ["close"],
  data() {
    const instanceId = ++modalInstanceCount;
    return {
      instanceId,
      previouslyFocusedElement: null,
      pendingFocusTarget: null,
      hasBodyScrollLock: false,
      isRegisteredOpen: false,
    };
  },
  computed: {
    computedWidth() {
      return { maxWidth: this.width };
    },
    titleId() {
      return `unit-modal-title-${this.instanceId}`;
    },
    descriptionId() {
      return `unit-modal-description-${this.instanceId}`;
    },
    resolvedLabelledby() {
      if (this.ariaLabelledby) return this.ariaLabelledby;
      return this.title ? this.titleId : undefined;
    },
    resolvedDescribedby() {
      if (this.ariaDescribedby) return this.ariaDescribedby;
      return this.description ? this.descriptionId : undefined;
    },
    resolvedAriaLabel() {
      if (this.resolvedLabelledby) return undefined;
      return this.ariaLabel || "Dialog";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.prepareDialog();
        } else {
          this.releaseDialog();
        }
      },
    },
  },
  mounted() {
    startInteractionTracking();
  },
  beforeUnmount() {
    this.releaseDialog();
    stopInteractionTracking();
  },
  methods: {
    focusableElements() {
      if (!this.$refs.dialog) return [];
      return Array.from(
        this.$refs.dialog.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((element) => element.getAttribute("aria-hidden") !== "true");
    },
    isTopmostDialog() {
      return (
        openDialogStack[openDialogStack.length - 1]?.id === this.instanceId
      );
    },
    registerOpenDialog() {
      if (this.isRegisteredOpen || typeof document === "undefined") return;
      const activationTarget = isConnectedFocusable(lastActivatedElement)
        ? lastActivatedElement
        : document.activeElement;
      if (openDialogStack.length === 0) {
        focusBeforeDialogStack = activationTarget;
      }
      this.previouslyFocusedElement = activationTarget;
      openDialogStack.push({
        id: this.instanceId,
        getDialogElement: () => this.$refs.dialog,
      });
      this.isRegisteredOpen = true;
    },
    unregisterOpenDialog() {
      if (!this.isRegisteredOpen) {
        return { wasTopmost: false, underlyingDialog: null, stackOrigin: null };
      }

      const wasTopmost = this.isTopmostDialog();
      const index = openDialogStack.findIndex(
        (entry) => entry.id === this.instanceId,
      );
      if (index >= 0) openDialogStack.splice(index, 1);
      this.isRegisteredOpen = false;
      const underlyingDialog =
        openDialogStack[openDialogStack.length - 1]?.getDialogElement() || null;
      const stackOrigin = focusBeforeDialogStack;
      if (openDialogStack.length === 0) focusBeforeDialogStack = null;
      return { wasTopmost, underlyingDialog, stackOrigin };
    },
    resolveInitialFocus() {
      if (this.initialFocusSelector && this.$refs.dialog) {
        try {
          const requestedTarget = this.$refs.dialog.querySelector(
            this.initialFocusSelector,
          );
          if (requestedTarget?.focus && !requestedTarget.disabled) {
            return requestedTarget;
          }
        } catch (_error) {
          // Invalid caller selectors fall back to the ordinary focus order.
        }
      }
      return this.focusableElements()[0] || this.$refs.dialog;
    },
    prepareDialog() {
      if (typeof document === "undefined") return;
      this.pendingFocusTarget = null;
      this.registerOpenDialog();
      if (!this.hasBodyScrollLock) {
        lockBodyScroll();
        this.hasBodyScrollLock = true;
      }

      this.$nextTick(() => {
        if (!this.visible || !this.isTopmostDialog()) return;
        this.resolveInitialFocus()?.focus();
      });
    },
    releaseDialog({ restoreFocus = true } = {}) {
      if (typeof document === "undefined") return;
      if (this.hasBodyScrollLock) {
        unlockBodyScroll();
        this.hasBodyScrollLock = false;
      }

      const returnTarget = this.previouslyFocusedElement;
      this.previouslyFocusedElement = null;
      const { wasTopmost, underlyingDialog, stackOrigin } =
        this.unregisterOpenDialog();
      if (!restoreFocus || !wasTopmost) return;

      this.$nextTick(() => {
        const underlyingElement = underlyingDialog;
        let target = returnTarget;
        if (underlyingElement && !underlyingElement.contains(returnTarget)) {
          target = underlyingElement;
        }
        if (!isConnectedFocusable(target)) target = stackOrigin;
        if (!isConnectedFocusable(target)) return;

        this.pendingFocusTarget = target;
        target.focus();
      });
    },
    restoreReleasedFocus() {
      if (this.visible) return;
      const target = this.pendingFocusTarget;
      this.pendingFocusTarget = null;
      if (isConnectedFocusable(target)) target.focus();
    },
    requestClose() {
      this.$emit("close");
    },
    handleBackdrop() {
      if (this.closeOnBackdrop && this.isTopmostDialog()) {
        this.requestClose();
      }
    },
    handleKeydown(event) {
      if (!this.isTopmostDialog()) return;
      if (event.key === "Escape") {
        if (!this.closeOnEscape) return;
        event.preventDefault();
        event.stopPropagation();
        this.requestClose();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = this.focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        this.$refs.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity var(--motion-standard) var(--motion-easing-standard);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  padding: var(--space-5);
  background-color: var(--color-overlay-scrim-strong);
}

.modal-backdrop--center {
  align-items: center;
}

.modal-backdrop--sheet {
  align-items: center;
}

.modal-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  color: var(--color-text-primary);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-surface-floating);
}

.modal-container--center {
  border-radius: var(--radius-lg);
}

.modal-container--sheet {
  border-radius: var(--radius-lg);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
}

.modal-title {
  margin: 0;
  font-size: var(--font-size-title);
  font-weight: var(--font-weight-semibold);
}

.modal-close {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: var(--control-height-default);
  height: var(--control-height-default);
  padding: 0;
  color: var(--color-icon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.modal-close:hover {
  transform: none;
}

svg.modal-close__icon {
  width: var(--control-height-dense);
  height: var(--control-height-dense);
}

.modal-description {
  margin-top: 0;
  color: var(--color-text-secondary);
}

.modal-body {
  padding: var(--space-5);
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-4);
  padding: var(--space-5);
  border-top: 1px solid var(--color-border-subtle);
}

</style>
